import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { WidgetT } from 'routes/Dashboard/components/WidgetsLayout/helpers';
import { useDispatch } from 'store/hooks';
import { useTranslation } from 'react-i18next';
import WidgetCard from '../WidgetCard';
import InternalLink from './InternalLink';
import ExternalLink from './ExternalLink';
import { NestedMenuOptionT } from 'ui/NestedMenu/NestedMenu';

type Props = {
    editMode: boolean;
    canEdit: boolean;
    widget: WidgetT;
    menuItems: NestedMenuOptionT[];
};

const StyOverflowingText = styled(Typography, {
    name: 'StyOverflowingText',
})({
    overflow: 'hidden',
    margin: 'auto 10px auto 10px',
    textAlign: 'center',
});

const NavLinkWidget = ({
    editMode,
    widget,
    menuItems,
    canEdit,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    // TODO StrictNullCheck : Try to remove "!"
    const { text, url, dashboardSlug } = widget.content!;
    const disabled = editMode || (!url && !dashboardSlug);
    const usedUrl =
        url ??
        (dashboardSlug
            ? `${window.location.origin}/dashboard/${dashboardSlug}`
            : undefined);

    const isInternalLink = useMemo(() => {
        if (usedUrl) {
            try {
                const parsedUrl = new URL(usedUrl);
                return parsedUrl.host === window.location.host;
            } catch (_TypeError) {
                dispatch.vision.notifyUser({
                    message: t('widget.navlink.urlError', { title: text }),
                    snackType: 'warning',
                });
            }
        }
        return '';
    }, [text, usedUrl]);

    const LinkComponent =
        isInternalLink && !disabled ? InternalLink : ExternalLink;
    return (
        <WidgetCard
            editMode={editMode}
            menuItems={menuItems}
            canEdit={canEdit}
            shadow
            sx={
                editMode
                    ? { backgroundColor: widget.content?.backgroundColor }
                    : undefined
            }
        >
            {editMode ? (
                <StyOverflowingText
                    variant="h5"
                    sx={{ color: widget.content?.fontColor }}
                >
                    {widget.content?.text || ''}
                </StyOverflowingText>
            ) : (
                <LinkComponent
                    url={usedUrl || ''}
                    disabled={disabled}
                    backgroundColor={widget.content?.backgroundColor}
                    fontColor={widget.content?.fontColor}
                >
                    <StyOverflowingText
                        variant="h5"
                        sx={{ color: widget.content?.fontColor }}
                    >
                        {widget.content?.text || ''}
                    </StyOverflowingText>
                </LinkComponent>
            )}
        </WidgetCard>
    );
};

export default React.memo(NavLinkWidget);
